import { useState } from 'react';
import {
  Button,
  IconButton,
  TableCell,
  TableRow,
  Typography,
  MenuItem,
  Divider,
} from '@mui/material';
import { ProductStatus } from '../../../@types/product';
import Iconify from '../../../components/iconify';
import ConfirmDialog from '../../../components/confirm-dialog/ConfirmDialog';
import { formatDate } from '@fullcalendar/core';
import MenuPopover from '../../../components/menu-popover';
import { getStockStatus } from '../../../utils/stockStatus';
import PendingStockLabel from '../stock/PendingStockLabel';
import { PendingAdjustmentSummary } from '../../../api/stock-adjustment.service';

type Props = {
  product: any;
  onEdit: VoidFunction;
  onDelete: VoidFunction;
  onPublish: VoidFunction;
  onView: VoidFunction;
  onAdjustStock: VoidFunction;
  /** Adjustments waiting for approval for this product. */
  pending?: PendingAdjustmentSummary;
};
export default function ProductTableRow({
  product,
  onEdit,
  onDelete,
  onPublish,
  onView,
  onAdjustStock,
  pending,
}: Props) {
  const brandName =
    product?.brand?.name ??
    product?.productDetails?.brandName ??
    (product?.brandId !== undefined && product?.brandId !== null
      ? String(product.brandId)
      : '');
  const supplierName =
    product?.supplier?.name ??
    product?.productDetails?.supplierName ??
    (product?.supplierId !== undefined && product?.supplierId !== null
      ? String(product.supplierId)
      : '');

  const [openConfirm, setOpenConfirm] = useState(false);

  const [openPublishConfirm, setOpenPublishConfirm] = useState(false);

  const stockStatus = getStockStatus(
    product.currentStock,
    (product as any).minimumStock,
  );
  const [openPopover, setOpenPopover] = useState<HTMLElement | null>(null);

  const handleCloseConfirm = () => {
    setOpenConfirm(false);
  };

  const handlePublishCloseConfirm = () => {
    setOpenPublishConfirm(false);
  };

  const handleOpenPopover = (event: React.MouseEvent<HTMLElement>) => {
    setOpenPopover(event.currentTarget);
  };

  const handleClosePopover = () => {
    setOpenPopover(null);
  };

  const handleOpenConfirm = () => {
    setOpenConfirm(true);
  };

  const handleOpenPublishConfirm = () => {
    setOpenPublishConfirm(true);
  };

  const formatEnumText = (text?: string) => {
    if (!text) return '';
    return text
      .split('_')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  return (
    <>
      <TableRow hover>
        <TableCell
          style={{ cursor: 'pointer' }}
          align="center"
          onClick={onView}
        >
          <Typography variant="subtitle2" noWrap>
            {product.name}
            {product.type && ` - ${formatEnumText(product.type)}`}
            {product.category && ` (${formatEnumText(product.category)})`}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="subtitle2" noWrap>
            {formatDate(product?.createdDate, {
              year: 'numeric',
              month: 'long',
              day: '2-digit',
            })}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="subtitle2" noWrap>
            {product.currentStock}
            <PendingStockLabel pending={pending} />
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="subtitle2" noWrap>
            {product.salePrice}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography variant="subtitle2" noWrap>
            {brandName || '-'}
          </Typography>
          <Typography variant="caption" noWrap color="text.secondary">
            {supplierName || '-'}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <Typography
            variant="subtitle2"
            noWrap
            sx={{ color: `${stockStatus.color}.main`, fontWeight: 'bold' }}
          >
            {stockStatus.label}
          </Typography>
        </TableCell>
        <TableCell align="center">
          <IconButton
            color={openPopover ? 'inherit' : 'default'}
            onClick={handleOpenPopover}
          >
            <Iconify icon="eva:more-vertical-fill" />
          </IconButton>
        </TableCell>
      </TableRow>
      <MenuPopover
        open={openPopover}
        onClose={handleClosePopover}
        arrow="right-top"
        sx={{ width: 160 }}
      >
        <MenuItem
          onClick={() => {
            onView();
            handleClosePopover();
          }}
        >
          <Iconify icon="eva:eye-fill" />
          View
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleClosePopover();
            onAdjustStock();
          }}
        >
          <Iconify icon="eva:edit-2-fill" />
          Quick Adjust
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleOpenPublishConfirm();
            handleClosePopover();
          }}
        >
          <Iconify
            icon={
              product.isActive
                ? 'eva:slash-fill'
                : 'eva:checkmark-circle-2-fill'
            }
          />
          {product.isActive ? 'Deactivate' : 'Activate'}
        </MenuItem>

        {/* <Divider sx={{ borderStyle: "dashed" }} />

        <MenuItem
          onClick={() => {
            handleOpenConfirm();
            handleClosePopover();
          }}
          sx={{ color: "error.main" }}>
          <Iconify icon="eva:trash-2-outline" />
          Delete
        </MenuItem> */}
      </MenuPopover>

      <ConfirmDialog
        open={openConfirm}
        onClose={handleCloseConfirm}
        title={`Delete Product`}
        content={`Are you sure want to delete  ${product.name}?`}
        action={
          <Button
            variant="contained"
            color="error"
            onClick={() => {
              onDelete();
              handleCloseConfirm();
            }}
          >
            Delete
          </Button>
        }
      />
      <ConfirmDialog
        open={openPublishConfirm}
        onClose={handlePublishCloseConfirm}
        title={`${product.isActive ? 'Deactivate' : 'Activate'} Product`}
        content={`Are you sure you want to ${
          product.isActive ? 'deactivate' : 'activate'
        } ${product.name}?`}
        action={
          <Button
            variant="contained"
            color="info"
            onClick={() => {
              onPublish();
              handlePublishCloseConfirm();
            }}
          >
            Save
          </Button>
        }
      />
    </>
  );
}
